import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ManagerRole } from '../../api/managerApi';
import { canAccessHomepageManagement, filterRoutesByRole } from '../../auth/permissions';
import { APP_ROUTES, type AppRouteId } from '../../routes/appRoutes';
import sidebarDownIcon from '../../assets/sidebar/SidebarDown.svg';
import sidebarUpIcon from '../../assets/sidebar/SidebarUp.svg';

const iconBase = {
  width: 17,
  height: 17,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const TrophyIcon = () => (
  <svg {...iconBase} aria-hidden="true">
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const CpuIcon = () => (
  <svg {...iconBase} aria-hidden="true">
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <rect x="9" y="9" width="6" height="6" />
    <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
  </svg>
);

const HomeIcon = () => (
  <svg {...iconBase} aria-hidden="true">
    <path d="M3 11 12 3l9 8" />
    <path d="M5 10v10h14V10" />
    <path d="M9 20v-6h6v6" />
  </svg>
);

const UsersIcon = () => (
  <svg {...iconBase} aria-hidden="true">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const iconById: Partial<Record<AppRouteId, ReactNode>> = {
  'event-list': <TrophyIcon />,
  'device-management': <CpuIcon />,
  'user-management': <UsersIcon />,
};

const homepageRouteIds: AppRouteId[] = [
  'notice-management',
  'b2b-inquiry-management',
  'qna-management',
  'certificate-template',
  'homepage-user-management',
  'goods-representative-image',
];
const homepageRouteIdSet = new Set<AppRouteId>(homepageRouteIds);

const upcomingHomepageMenu: string[] = [];

// 대회 진입 전 전역 관리 메뉴 (admin 섹션)
const roleLabelByValue: Record<ManagerRole, string> = {
  SUPER: 'SUPER ADMIN',
  MIDDLE: 'MIDDLE ADMIN',
  GENERAL: 'GENERAL ADMIN',
  NORMAL: 'USER',
};

const globalMenu = APP_ROUTES.filter((route) => route.section === 'admin');

type GlobalSidebarProps = Readonly<{
  activePath: string;
  role: ManagerRole | null;
}>;

function GlobalSidebar({ activePath, role }: GlobalSidebarProps) {
  const visibleMenu = filterRoutesByRole(globalMenu, role);
  const mainMenu = visibleMenu.filter((route) => !homepageRouteIdSet.has(route.id));
  const homepageMenu = canAccessHomepageManagement(role)
    ? visibleMenu.filter((route) => homepageRouteIdSet.has(route.id))
    : [];
  const homepageMenuCount = homepageMenu.length + upcomingHomepageMenu.length;
  const isHomepageActive = homepageMenu.some((route) => route.path === activePath);
  const [isHomepageOpen, setIsHomepageOpen] = useState(isHomepageActive);

  useEffect(() => {
    if (isHomepageActive) {
      setIsHomepageOpen(true);
    }
  }, [isHomepageActive]);

  return (
    <aside className="sidebar global-sidebar" aria-label="전역 관리 메뉴">
      <div className="global-sidebar-role">
        <span className="global-sidebar-role-label">현재 권한</span>
        <span className="global-sidebar-role-value">
          {role ? roleLabelByValue[role] : '확인 중'}
        </span>
      </div>
      <nav>
        {mainMenu.map((route) => (
          <Link
            key={route.path}
            className={`gsb-item${route.path === activePath ? ' active' : ''}`}
            to={route.path}
          >
            <span className="gsb-icon">{iconById[route.id]}</span>
            <span>{route.label}</span>
          </Link>
        ))}
        {homepageMenu.length > 0 && (
          <section className="nav-section global-homepage-section">
            <button
              aria-expanded={isHomepageOpen}
              className="nav-heading"
              onClick={() => setIsHomepageOpen((current) => !current)}
              type="button"
            >
              <span className="heading-left">
                <span className="gsb-icon">
                  <HomeIcon />
                </span>
                <span>홈페이지 관리</span>
              </span>
              <span className="heading-right">
                <span className="count">{homepageMenuCount}</span>
                <img
                  alt=""
                  aria-hidden="true"
                  className="sidebar-chevron"
                  src={isHomepageOpen ? sidebarUpIcon : sidebarDownIcon}
                />
              </span>
            </button>

            {isHomepageOpen && (
              <div className="subnav">
                {homepageMenu.map((route) => (
                  <Link
                    key={route.path}
                    className={`subnav-item${route.path === activePath ? ' active' : ''}`}
                    to={route.path}
                  >
                    <span>{route.label}</span>
                  </Link>
                ))}
                {upcomingHomepageMenu.map((label) => (
                  <button className="subnav-item subnav-item-disabled" disabled key={label} type="button">
                    <span>{label}</span>
                    <span className="upcoming-badge">예정</span>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}
      </nav>
    </aside>
  );
}

export default GlobalSidebar;
